import styled from '@emotion/styled';
import { useAtom } from 'jotai';
import { Location } from 'react-router-dom';
import { SuperAdminWorkspace, RIGHT_SIDEBAR_ACTION } from '@@types/index';
import { Color } from '@resources/colors';
import { colFlex, rowFlex } from '@styles/flexStyles';
import { formatKoreanDate } from '@utils/formatNumber';
import { externalSidebarAtom } from '@jotai/atoms';
import { SUPER_ADMIN_ROUTES } from '@constants/routes';
import OnboardingBadge from './OnboardingBadge';
import WorkspaceDetailContent from './WorkspaceDetailContent';

const Container = styled.div`
  width: 100%;
  cursor: pointer;
  padding: 12px 0;
  border-bottom: 1px solid ${Color.LIGHT_GREY};
  ${rowFlex({ align: 'center', justify: 'space-between' })}

  &:hover .ws-name {
    color: ${Color.KIO_ORANGE};
  }
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  gap: 2px;
  ${colFlex({ justify: 'center' })}
`;

const RightInfo = styled.div`
  margin-left: 12px;
  ${rowFlex({ align: 'center', justify: 'flex-end' })}
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Color.BLACK};
  transition: color 0.15s;
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${Color.HEAVY_GREY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface SuperAdminWorkspaceContentProps {
  workspace: SuperAdminWorkspace;
  onChanged: () => void;
}

function SuperAdminWorkspaceContent({ workspace, onChanged }: SuperAdminWorkspaceContentProps) {
  const [externalSidebar, setExternalSidebar] = useAtom(externalSidebarAtom);

  const handleClick = () => {
    const isSameOpen = externalSidebar.action === RIGHT_SIDEBAR_ACTION.OPEN && externalSidebar.title === workspace.name;
    if (isSameOpen) {
      setExternalSidebar({ action: RIGHT_SIDEBAR_ACTION.CLOSE });
      return;
    }

    setExternalSidebar({
      action: RIGHT_SIDEBAR_ACTION.OPEN,
      title: workspace.name,
      content: (
        <WorkspaceDetailContent
          key={workspace.id}
          workspace={workspace}
          onClose={() => setExternalSidebar({ action: RIGHT_SIDEBAR_ACTION.CLOSE })}
          onChanged={onChanged}
        />
      ),
      location: { pathname: SUPER_ADMIN_ROUTES.WORKSPACE } as Location,
    });
  };

  return (
    <Container onClick={handleClick}>
      <Info>
        <Name className="ws-name">{workspace.name}</Name>
        <Sub>
          {formatKoreanDate(workspace.createdAt)} | {workspace.schoolName} | {workspace.owner.name} ({workspace.owner.loginId})
        </Sub>
      </Info>
      <RightInfo>
        <OnboardingBadge done={!workspace.isOnboarding} />
      </RightInfo>
    </Container>
  );
}

export default SuperAdminWorkspaceContent;
